import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  IconButton,
  Tooltip,
} from "@mui/material";
import {
  fetchAllowedCertifications,
  createAllowedCertification,
  updateAllowedCertification,
  deleteAllowedCertification,
} from "../api/apiClient";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SchoolIcon from "@mui/icons-material/School";
import { useTheme } from "@mui/material/styles";
import { useActiveTenant } from "../hooks/useActiveTenant";
import NoTenantSelected from "../components/NoTenantSelected";

const DS = {
  surfaceContainerLowest: "#ffffff",
  surfaceContainerLow: "var(--color-surface-press-light)",
  surfaceContainerHigh: "var(--color-hairline-cloud)",
  onSurface: "#191c1e",
  onSurfaceVariant: "var(--color-ink-2)",
  outlineVariant: "var(--color-hairline-cloud)",
};

const STATUS_CFG = {
  expired: {
    dsText: "var(--color-danger)",
    dsBg: "rgba(186, 26, 26, 0.12)",
  },
};

function CertificationTypeManagementView({ user, tenantId }) {
  const theme = useTheme();
  const tPrimary = theme.palette.primary.main;
  const tPrimaryDark = theme.palette.primary.dark;
  const [certTypes, setCertTypes] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    name: "",
    issuer: "",
    validityYears: "",
    neverExpires: false,
    description: "",
  });

  const { tenantId: activeTenantId } = useActiveTenant();
  const effectiveTenantId = tenantId || activeTenantId;

  const loadTypes = useCallback(() => {
    if (!effectiveTenantId) return;
    fetchAllowedCertifications(effectiveTenantId).then(setCertTypes);
  }, [effectiveTenantId]);

  useEffect(() => {
    loadTypes();
  }, [loadTypes]);

  function handleSave() {
    const data = {
      ...form,
      requiresExpiry: !form.neverExpires,
      validityYears:
        form.neverExpires || !form.validityYears
          ? null
          : Number(form.validityYears),
    };
    if (!effectiveTenantId) return;
    const promise = editing
      ? updateAllowedCertification(effectiveTenantId, editing.id, data)
      : createAllowedCertification(effectiveTenantId, data);
    promise.then(() => {
      loadTypes();
      setDialogOpen(false);
      setEditing(null);
      setForm({ name: "", issuer: "", validityYears: "", description: "" });
    });
  }

  function handleDelete(id) {
    if (!effectiveTenantId) return;
    if (window.confirm("Remove this certification type?")) {
      deleteAllowedCertification(effectiveTenantId, id).then(loadTypes);
    }
  }

  function openAdd() {
    setEditing(null);
    setForm({
      name: "",
      issuer: "",
      validityYears: "",
      neverExpires: false,
      description: "",
    });
    setDialogOpen(true);
  }

  function openEdit(ct) {
    setEditing(ct);
    setForm({
      name: ct.name || "",
      issuer: ct.issuer || "",
      validityYears: ct.validityYears ?? "",
      neverExpires: ct.validityYears == null,
      description: ct.description || "",
    });
    setDialogOpen(true);
  }

  if (!effectiveTenantId) {
    return (
      <Box>
        <NoTenantSelected message="Select a training center from the top bar before managing certification types." />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ mb: 4 }}>
        <Typography
          variant="h4"
          fontWeight={800}
          color={tPrimary}
          sx={{ mb: 1, fontFamily: "var(--font-display)" }}
        >
          Manage Certification Types
        </Typography>
        <Typography variant="body1" color={DS.onSurfaceVariant}>
          Configure the certifications that members can earn and track in your
          training center.
        </Typography>
      </Box>

      <Paper
        elevation={0}
        sx={{
          mb: 3,
          p: 3,
          borderRadius: "12px",
          bgcolor: DS.surfaceContainerLowest,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Box>
          <Typography
            variant="h6"
            fontWeight={700}
            color={DS.onSurface}
            sx={{ fontFamily: "var(--font-display)" }}
          >
            {certTypes.length} Certification Types
          </Typography>
          <Typography variant="body2" color={DS.onSurfaceVariant}>
            Define certification requirements, validity periods, and issuing
            authorities.
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={openAdd}
          sx={{
            borderRadius: "8px",
            bgcolor: tPrimary,
            fontWeight: 600,
            "&:hover": { bgcolor: tPrimaryDark },
          }}
        >
          Add Certification Type
        </Button>
      </Paper>

      <TableContainer
        component={Paper}
        sx={{
          borderRadius: "12px",
          bgcolor: DS.surfaceContainerLowest,
          boxShadow: "none",
          border: `1px solid ${DS.surfaceContainerHigh}`,
        }}
      >
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: DS.surfaceContainerLow }}>
              <TableCell sx={{ fontWeight: 700, py: 2.5, color: DS.onSurface }}>
                Name
              </TableCell>
              <TableCell sx={{ fontWeight: 700, py: 2.5, color: DS.onSurface }}>
                Issuing Authority
              </TableCell>
              <TableCell sx={{ fontWeight: 700, py: 2.5, color: DS.onSurface }}>
                Validity
              </TableCell>
              <TableCell sx={{ fontWeight: 700, py: 2.5, color: DS.onSurface }}>
                Description
              </TableCell>
              <TableCell
                sx={{
                  fontWeight: 700,
                  py: 2.5,
                  textAlign: "right",
                  color: DS.onSurface,
                }}
              >
                Actions
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {certTypes.map((ct) => (
              <TableRow
                key={ct.id}
                hover
                sx={{
                  "&:hover": { bgcolor: DS.surfaceContainerLow },
                }}
              >
                <TableCell>
                  <Typography fontWeight={700} color={DS.onSurface}>
                    {ct.name}
                  </Typography>
                </TableCell>
                <TableCell sx={{ color: DS.onSurfaceVariant }}>
                  {ct.issuer || "—"}
                </TableCell>
                <TableCell sx={{ color: DS.onSurfaceVariant }}>
                  {ct.validityYears
                    ? `${ct.validityYears} years`
                    : "No expiration"}
                </TableCell>
                <TableCell>
                  <Typography
                    variant="body2"
                    color={DS.onSurfaceVariant}
                    noWrap
                    sx={{ maxWidth: 300 }}
                  >
                    {ct.description || "—"}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Stack direction="row" spacing={1} justifyContent="flex-end">
                    <Tooltip title="Edit">
                      <IconButton
                        size="small"
                        onClick={() => openEdit(ct)}
                        sx={{
                          color: tPrimary,
                          "&:hover": { bgcolor: "rgba(0, 56, 108, 0.08)" },
                        }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton
                        size="small"
                        onClick={() => handleDelete(ct.id)}
                        sx={{
                          color: STATUS_CFG.expired.dsText,
                          "&:hover": { bgcolor: STATUS_CFG.expired.dsBg },
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
            {certTypes.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 6 }}>
                  <SchoolIcon
                    sx={{ fontSize: 48, color: DS.onSurfaceVariant, mb: 2 }}
                  />
                  <Typography color={DS.onSurfaceVariant}>
                    No certification types defined yet.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "12px",
            bgcolor: DS.surfaceContainerLowest,
          },
        }}
      >
        <DialogTitle
          sx={{
            pb: 1,
            pt: 3,
            px: 3,
            fontWeight: 700,
            color: tPrimary,
            fontFamily: "var(--font-display)",
          }}
        >
          {editing ? "Edit Certification Type" : "Add Certification Type"}
        </DialogTitle>
        <DialogContent sx={{ px: 3 }}>
          <Stack spacing={2.5} sx={{ mt: 1 }}>
            <TextField
              label="Certification Name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              fullWidth
              required
              placeholder="e.g., Firefighter I"
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "8px",
                  bgcolor: DS.surfaceContainerLow,
                  "& fieldset": { borderColor: "transparent" },
                  "&:hover fieldset": { borderColor: "rgba(0, 56, 108, 0.25)" },
                  "&.Mui-focused fieldset": {
                    borderColor: tPrimary,
                    borderWidth: "2px",
                  },
                },
              }}
            />
            <TextField
              label="Issuing Authority"
              value={form.issuer}
              onChange={(e) => setForm({ ...form, issuer: e.target.value })}
              fullWidth
              placeholder="e.g., State Fire Marshal"
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "8px",
                  bgcolor: DS.surfaceContainerLow,
                  "& fieldset": { borderColor: "transparent" },
                  "&:hover fieldset": { borderColor: "rgba(0, 56, 108, 0.25)" },
                  "&.Mui-focused fieldset": {
                    borderColor: tPrimary,
                    borderWidth: "2px",
                  },
                },
              }}
            />
            <TextField
              label="Validity Period (years)"
              type="number"
              value={form.validityYears}
              onChange={(e) =>
                setForm({ ...form, validityYears: e.target.value })
              }
              fullWidth
              disabled={form.neverExpires}
              helperText={
                form.neverExpires
                  ? "This certification will not expire."
                  : "Leave blank if certification does not expire"
              }
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "8px",
                  bgcolor: DS.surfaceContainerLow,
                  "& fieldset": { borderColor: "transparent" },
                  "&:hover fieldset": { borderColor: "rgba(0, 56, 108, 0.25)" },
                  "&.Mui-focused fieldset": {
                    borderColor: tPrimary,
                    borderWidth: "2px",
                  },
                },
              }}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={form.neverExpires}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      neverExpires: e.target.checked,
                      validityYears: e.target.checked ? "" : form.validityYears,
                    })
                  }
                  color="primary"
                />
              }
              label="No expiration"
              sx={{ color: DS.onSurfaceVariant }}
            />
            <TextField
              label="Description"
              value={form.description}
              onChange={(e) =>
                setForm({ ...form, description: e.target.value })
              }
              fullWidth
              multiline
              rows={2}
              placeholder="Brief description of requirements..."
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "8px",
                  bgcolor: DS.surfaceContainerLow,
                  "& fieldset": { borderColor: "transparent" },
                  "&:hover fieldset": { borderColor: "rgba(0, 56, 108, 0.25)" },
                  "&.Mui-focused fieldset": {
                    borderColor: tPrimary,
                    borderWidth: "2px",
                  },
                },
              }}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, pt: 1 }}>
          <Button
            onClick={() => setDialogOpen(false)}
            variant="outlined"
            size="large"
            sx={{ borderRadius: "8px" }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={!form.name.trim()}
            size="large"
            sx={{
              borderRadius: "8px",
              bgcolor: tPrimary,
              "&:hover": { bgcolor: tPrimaryDark },
            }}
          >
            {editing ? "Save Changes" : "Create Type"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export function AdminCertificationTypesView(props) {
  return <CertificationTypeManagementView {...props} />;
}

export function CoordinatorCertificationTypesView(props) {
  return <CertificationTypeManagementView {...props} />;
}
